// src/services/authService.js
// ─────────────────────────────────────────────────────────────────
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";

/**
 * Register a new customer account.
 */
export async function register(email, password, displayName) {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  // Update display name
  await updateProfile(user, { displayName });

  // Create user document in Firestore
  await setDoc(doc(db, "users", user.uid), {
    uid: user.uid,
    email,
    displayName,
    role: "customer",
    totalOrders: 0,
    createdAt: serverTimestamp(),
  });

  return user;
}

/**
 * Sign in with email and password.
 */
export async function login(email, password) {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
}

/**
 * Sign out the current user.
 */
export async function logout() {
  await signOut(auth);
}

/**
 * Send a password reset email.
 */
export async function resetPassword(email) {
  await sendPasswordResetEmail(auth, email);
}

/**
 * Get the Firestore user profile for the current user.
 * Wrapped in try/catch to prevent app-wide crashes if DB is missing.
 */
export async function getUserProfile(uid) {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    return snap.exists() ? snap.data() : null;
  } catch (error) {
    console.error("AuthService: Error fetching user profile:", error.message);
    // Returning null allows AuthContext to continue and stop the 'loading' state
    return null; 
  }
}

/**
 * Check if the current user is an admin (via custom claim).
 * Must force token refresh after role is set.
 */
export async function isAdmin() {
  const user = auth.currentUser;
  if (!user) return false;
  const idTokenResult = await user.getIdTokenResult(true);
  return idTokenResult.claims.role === "admin";
}

/**
 * Subscribe to auth state changes.
 */
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}
